/**
 * Users repository: CRUD on `users/{uid}`. The legacy code reads/writes this
 * path from a dozen different files; this is the single entry point.
 *
 * Returned shapes are minimally typed — callers should expect missing fields
 * when records are partially written by older code paths.
 */

import { ref, readOnce } from '../../core/firebase.js';

/**
 * @typedef {Object} UserRecord
 * @property {string} [uid]
 * @property {string} [phone]
 * @property {string} [plate]
 * @property {string} [carYear]
 * @property {boolean} [blocked]
 * @property {string} [blockReason]
 * @property {{ forced?: boolean; reason?: string }} [forceLogout]
 * @property {{ deviceId?: string }} [lastActiveDevice]
 * @property {number} [updatedAt]
 */

/**
 * @param {string} uid
 * @returns {Promise<UserRecord | null>}
 */
export function get(uid) {
  if (!uid) return Promise.resolve(null);
  return readOnce(`users/${uid}`);
}

/**
 * Patch a user record. Top-level keys not present in `patch` are left intact.
 * @param {string} uid
 * @param {Partial<UserRecord> & Record<string, unknown>} patch
 * @returns {Promise<void>}
 */
export function update(uid, patch) {
  if (!uid) return Promise.reject(new Error('users.update: uid required'));
  return ref(`users/${uid}`).update({ ...patch, updatedAt: Date.now() });
}

/**
 * Subscribe to live updates of the user record. Returns an unsubscribe function.
 * @param {string} uid
 * @param {(record: UserRecord | null) => void} handler
 * @returns {() => void}
 */
export function subscribe(uid, handler) {
  if (!uid) return () => {};
  const r = ref(`users/${uid}`);
  const cb = (/** @type {any} */ snap) => handler(snap.val() ?? null);
  r.on('value', cb);
  return () => { try { r.off('value', cb); } catch { /* ignore */ } };
}

/**
 * Check whether a user is blocked or has been force-logged-out. Returns the
 * reason, or null if neither condition is set.
 * @param {string} uid
 * @returns {Promise<{ kind: 'blocked' | 'forceLogout'; reason: string } | null>}
 */
export async function getBlockStatus(uid) {
  const record = await get(uid);
  if (!record) return null;
  if (record.blocked === true) {
    return { kind: 'blocked', reason: record.blockReason || 'הפרת תנאי שימוש' };
  }
  if (record.forceLogout?.forced === true) {
    const code = record.forceLogout.reason || '';
    const reason =
      code === 'blocked_by_admin' ? 'החשבון נחסם' :
      code === 'deleted_by_admin' ? 'החשבון נמחק' :
      'ניתוק על ידי מנהל';
    return { kind: 'forceLogout', reason };
  }
  return null;
}

/**
 * Record this device as the user's `lastActiveDevice`. Sets a touch timestamp
 * the watcher can listen on without needing extra writes.
 * @param {string} uid
 * @param {string} deviceId
 * @returns {Promise<void>}
 */
export function setLastActiveDevice(uid, deviceId) {
  if (!uid || !deviceId) return Promise.resolve();
  return ref(`users/${uid}/lastActiveDevice`).set({
    deviceId,
    at: Date.now(),
  });
}
